import { Injectable, signal } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import { QUESTIONNAIRES_SEED } from '@common/data';
import type {
  Questionnaire,
  QuestionnairePage,
  QuestionnaireQuery,
  QuestionnaireStatus,
  QuestionnaireSummaryChange,
} from '@common/types';

const LIST_LATENCY_MS = 200;
const SAVE_LATENCY_MS = 250;

@Injectable({ providedIn: 'root' })
export class QuestionnairesService {
  private readonly _questionnaires = signal<Questionnaire[]>(
    QUESTIONNAIRES_SEED.map((questionnaire) => ({ ...questionnaire })),
  );
  private readonly _revision = signal<number>(0);
  private seq = 0;

  readonly revision = this._revision.asReadonly();
  readonly all = this._questionnaires.asReadonly();

  getById(id: string): Questionnaire | undefined {
    return this._questionnaires().find((questionnaire) => questionnaire.id === id);
  }

  list(query: QuestionnaireQuery): Observable<QuestionnairePage> {
    let result = this._questionnaires().slice();

    const term = query.search.trim().toLowerCase();
    if (term.length > 0) {
      result = result.filter(
        (questionnaire) =>
          questionnaire.name.toLowerCase().includes(term) ||
          questionnaire.title.toLowerCase().includes(term) ||
          questionnaire.id.includes(term),
      );
    }

    result = this.applyInFilter(result, query.filters['type'], (q) => q.type);
    result = this.applyInFilter(result, query.filters['status'], (q) => q.status);

    if (query.sortField) {
      const direction = query.sortOrder === 'desc' ? -1 : 1;
      result.sort((a, b) => this.compare(a, b, query.sortField!) * direction);
    }

    const total = result.length;
    const start = Math.max(0, query.first);
    const data = result.slice(start, start + Math.max(1, query.rows));

    return of({ data, total }).pipe(delay(LIST_LATENCY_MS));
  }

  create(name: string, type: Questionnaire['type']): Observable<Questionnaire> {
    const now = new Date().toISOString();
    this.seq += 1;
    const created: Questionnaire = {
      id: this.nextId(),
      name: name.trim() || 'Untitled questionnaire',
      title: '',
      type,
      status: 'draft',
      isDefault: false,
      pageCount: 0,
      createdAtIso: now,
    };
    this._questionnaires.update((rows) => [created, ...rows]);
    this.bump();
    return of(created).pipe(delay(SAVE_LATENCY_MS));
  }

  setStatus(id: string, status: QuestionnaireStatus): Observable<Questionnaire> {
    return this.patch(id, { status });
  }

  /** Writes the summary fields the builder owns once a draft is saved. */
  saveSummary(id: string, change: QuestionnaireSummaryChange): Observable<Questionnaire> {
    return this.patch(id, change);
  }

  makeDefault(id: string): Observable<Questionnaire> {
    const target = this.getById(id);
    if (!target) return throwError(() => new Error('Questionnaire not found.'));
    this._questionnaires.update((rows) =>
      rows.map((questionnaire) => ({
        ...questionnaire,
        isDefault: questionnaire.id === id,
        status: questionnaire.id === id ? 'active' : questionnaire.status,
      })),
    );
    this.bump();
    return of(this.getById(id)!).pipe(delay(SAVE_LATENCY_MS));
  }

  duplicate(id: string): Observable<Questionnaire> {
    const source = this.getById(id);
    if (!source) return throwError(() => new Error('Questionnaire not found.'));
    const copy: Questionnaire = {
      ...source,
      id: this.nextId(),
      name: `${source.name} (Copy)`,
      status: 'draft',
      isDefault: false,
      createdAtIso: new Date().toISOString(),
    };
    this._questionnaires.update((rows) => [copy, ...rows]);
    this.bump();
    return of(copy).pipe(delay(SAVE_LATENCY_MS));
  }

  remove(id: string): Observable<void> {
    const target = this.getById(id);
    if (!target) return throwError(() => new Error('Questionnaire not found.'));
    if (target.isDefault) {
      return throwError(() => new Error('The default questionnaire cannot be deleted.'));
    }
    this._questionnaires.update((rows) => rows.filter((questionnaire) => questionnaire.id !== id));
    this.bump();
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }

  syncPageCount(id: string, pageCount: number): void {
    const target = this.getById(id);
    if (!target || target.pageCount === pageCount) return;
    this._questionnaires.update((rows) =>
      rows.map((questionnaire) =>
        questionnaire.id === id ? { ...questionnaire, pageCount } : questionnaire,
      ),
    );
    this.bump();
  }

  private patch(id: string, change: Partial<Questionnaire>): Observable<Questionnaire> {
    const target = this.getById(id);
    if (!target) return throwError(() => new Error('Questionnaire not found.'));
    const next: Questionnaire = { ...target, ...change };
    this._questionnaires.update((rows) =>
      rows.map((questionnaire) => (questionnaire.id === id ? next : questionnaire)),
    );
    this.bump();
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  private applyInFilter(
    rows: Questionnaire[],
    values: ReadonlyArray<string> | undefined,
    accessor: (questionnaire: Questionnaire) => string,
  ): Questionnaire[] {
    if (!values?.length) return rows;
    const allowed = new Set(values);
    return rows.filter((questionnaire) => allowed.has(accessor(questionnaire)));
  }

  private compare(a: Questionnaire, b: Questionnaire, field: string): number {
    const av = this.sortValue(a, field);
    const bv = this.sortValue(b, field);
    if (av < bv) return -1;
    if (av > bv) return 1;
    return 0;
  }

  private sortValue(questionnaire: Questionnaire, field: string): string | number {
    switch (field) {
      case 'pageCount':
        return questionnaire.pageCount;
      case 'createdAtIso':
        return questionnaire.createdAtIso;
      case 'type':
        return questionnaire.type;
      case 'status':
        return questionnaire.status;
      case 'id':
        return Number(questionnaire.id);
      default:
        return questionnaire.name.toLowerCase();
    }
  }

  private bump(): void {
    this._revision.update((n) => n + 1);
  }

  private nextId(): string {
    const ids = this._questionnaires().map((questionnaire) => Number(questionnaire.id));
    return String(Math.max(1000, ...ids) + 1);
  }
}
